import classNames from "classnames";
import { Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";

import { SlideClassification } from "pwapi";

import { showMask } from "shared/actions/slideDetailActions";
import { LESION_LINE_NAMES } from "shared/reducers/aiReducer";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";

import Spinner from "components/Spinner";
import { useHasAILoaded } from "hooks/aiHooks";
import ApplicationState from "store/ApplicationState";
import { useAppSelector } from "store/hooks";
import { segregratedClassificationLines } from "util/aiUtils";

import SubtypesAndMargin from "./ai/SubtypesAndMargin";
import Sidebar from "./Sidebar";

type AIOverlayDialogProps = {
  slideDetailObject: SlideDetailState;
  aiObject: SlideClassification;
};

const AIOverlayDialog = ({
  slideDetailObject,
  aiObject,
}: AIOverlayDialogProps) => {
  const dispatch = useDispatch();

  const features: FeatureFlags = useSelector(
    ({ sys }: ApplicationState) => sys.features
  );
  const { maskLesionLineType } = useAppSelector((app) => app.ai);

  const {
    lesionLineByLesionLineType,
    positiveLesionLineTypes,
    negativeLesionLineTypes,
    inconclusiveLesionLineTypes,
    notRunLesionLineTypes,
  } = segregratedClassificationLines(aiObject);

  const aiLoaded = useHasAILoaded(slideDetailObject);

  return (
    <Sidebar isWide>
      {aiLoaded ? ( // TODO: useLoaded instead here
        <div className="Sidebar__contentContainer">
          <dl>
            <dd>
              <div className="LesionLineType">
                <label>
                  <input
                    type="radio"
                    id="no-overlay"
                    name="ai-overlay"
                    value="no-overlay"
                    checked={maskLesionLineType === undefined}
                    onChange={() => {
                      dispatch(showMask(undefined));
                    }}
                  />
                  No Overlay
                </label>
              </div>
            </dd>

            {[
              { name: "POSITIVE DIAGNOSES", types: positiveLesionLineTypes },
              { name: "INCONCLUSIVE", types: inconclusiveLesionLineTypes },
              { name: "NEGATIVE DIAGNOSES", types: negativeLesionLineTypes },
              { name: "NOT RUN", types: notRunLesionLineTypes },
            ].map((overlay) => {
              const { name: resultDescription, types: lesionLineTypes } =
                overlay;
              return (
                lesionLineTypes.length > 0 && (
                  <Fragment key={resultDescription}>
                    <dt>{resultDescription}</dt>
                    <dd>
                      {lesionLineTypes.map((lesionLineType) => {
                        const lesionLine =
                          lesionLineByLesionLineType.get(lesionLineType);
                        return (
                          <div
                            key={lesionLineType}
                            className={classNames("LesionLineType", {
                              "LesionLineType--selected":
                                maskLesionLineType === lesionLineType,
                            })}
                          >
                            <label>
                              {resultDescription !== "NOT RUN" && (
                                <input
                                  type="radio"
                                  name="ai-overlay"
                                  value={lesionLineType}
                                  checked={
                                    maskLesionLineType === lesionLineType
                                  }
                                  onChange={() => {
                                    dispatch(showMask(lesionLineType));
                                  }}
                                />
                              )}
                              <div>
                                <strong className="LesionLineType__name">
                                  <span className="LesionLineType__legend">
                                    {LESION_LINE_NAMES[lesionLineType]}
                                    {lesionLine?.margin_status && (
                                      <>
                                        {" "}
                                        &ndash; {lesionLine.margin_status}{" "}
                                        Margin
                                      </>
                                    )}
                                  </span>
                                </strong>

                                {features.canViewAIScores &&
                                  lesionLineByLesionLineType.has(
                                    lesionLineType
                                  ) && (
                                    <span className="LesionLineType__score">
                                      &nbsp;&nbsp;(SCORE:&nbsp;
                                      {Math.round(
                                        1000 *
                                          (lesionLineByLesionLineType.get(
                                            lesionLineType
                                          )?.wsi_lvl_prediction || 0)
                                      ) / 1000}
                                      )
                                    </span>
                                  )}

                                {lesionLine && (
                                  <span className="LesionLineType__subtype">
                                    <SubtypesAndMargin
                                      lesionLine={lesionLine}
                                      showInconclusive={true}
                                    />
                                  </span>
                                )}
                              </div>
                            </label>
                          </div>
                        );
                      })}
                    </dd>
                  </Fragment>
                )
              );
            })}
          </dl>
        </div>
      ) : (
        <Spinner // TODO: replace this Spinner with useIsLoading / or whatever
          size="24px"
          width="3px"
          frontColor="var(--teal)"
          backColor="var(--lightNoteGreen)"
        />
      )}
    </Sidebar>
  );
};

export default AIOverlayDialog;
